import {
	Controls,
	MediaPlayer,
	MediaProvider,
	type MediaPlayerInstance,
	type MediaTimeUpdateEventDetail,
	Tooltip,
	SeekButton,
} from "@vidstack/react";
import { useEffect, useMemo, useState } from "react";
import { Subject, throttleTime } from "rxjs";
import type { EventListTeamInfo, GameClipResponse, MergedGpEventResponse } from "../../../types";
import "./clips.css";
import { DefaultAudioLayout, DefaultVideoLayout, defaultLayoutIcons } from "@vidstack/react/player/layouts/default";
import { ClipEvents } from "./ClipEvents";
import { EventInVideo } from "../EventList/EventInVideo";
import { useRestoreScrollOnFullscreenExit } from "../../../useRestoreScrollOnFullscreenExit";
import { useVideoPlayerVolumeStore } from "../../../../video-player/video-player-volume-store";

interface ClipPlayerProps {
	currentTime: number;
	setCurrentTime: (currentTime: number) => void;
	playerRef: React.RefObject<MediaPlayerInstance>;
	clip: GameClipResponse;
	mergedEvents: MergedGpEventResponse[];
	showSidebar: boolean;
	setShowSidebar: (show: boolean) => void;
	teamInfo: EventListTeamInfo;
	eventCount: number;
}

export function ClipPlayer({
	eventCount,
	teamInfo,
	setCurrentTime,
	currentTime,
	playerRef,
	clip,
	mergedEvents,
	showSidebar,
	setShowSidebar,
}: ClipPlayerProps) {
	const [fullscreen, setFullscreen] = useState<boolean>();
	const volume = useVideoPlayerVolumeStore((state) => state.volume);
	const muted = useVideoPlayerVolumeStore((state) => state.muted);
	const setVolume = useVideoPlayerVolumeStore((state) => state.setVolume);
	const timeSubject = useMemo(() => new Subject<number>(), []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const subscription = timeSubject.pipe(throttleTime(150)).subscribe((time) => {
			setCurrentTime(time);
		});
		return () => subscription.unsubscribe();
	}, [timeSubject]);

	function onTimeUpdate(nativeEventDetail: MediaTimeUpdateEventDetail) {
		if (nativeEventDetail.currentTime) {
			timeSubject.next(nativeEventDetail.currentTime * 1000);
		}
	}

	useEffect(() => {
		if (fullscreen !== undefined) {
			const player = document.getElementById("player");
			player?.focus();
		}
	}, [fullscreen]);

	useRestoreScrollOnFullscreenExit(fullscreen);

	return (
		<div className="clipPlayer-container">
			<MediaPlayer
				id="player"
				ref={playerRef}
				muted={muted}
				volume={volume}
				onVolumeChange={setVolume}
				src={clip.url ?? ""}
				onFullscreenChange={(value) => setFullscreen(value)}
				fullscreenOrientation="none"
				onTimeUpdate={onTimeUpdate}
				playsInline
			>
				<MediaProvider>{!showSidebar && <EventInVideo events={clip.events} isClip={true} playerRef={playerRef} />}</MediaProvider>
				{clip.events.length > 0 && showSidebar && fullscreen && (
					<div className="clipPlayer-sidebar">
						<ClipEvents
							eventCount={eventCount}
							teamInfo={teamInfo}
							setShowSidebar={setShowSidebar}
							currentTime={currentTime}
							mergedEvents={mergedEvents}
							playerRef={playerRef}
						/>
					</div>
				)}
				<Controls.Root className="vds-controls">
					<Controls.Group className="vds-controls-group top" style={fullscreen && showSidebar ? { width: "calc(100% - 420px)" } : {}}>
						{clip.events.length > 0 && fullscreen && (
							<div
								className="showMenuButton"
								onClick={() => {
									setShowSidebar(!showSidebar);
								}}
							>
								<span className="icon-new icon-video-list icon-20" />
							</div>
						)}
					</Controls.Group>
				</Controls.Root>
				<DefaultAudioLayout icons={defaultLayoutIcons} />
				<DefaultVideoLayout
					icons={defaultLayoutIcons}
					playbackRates={{ min: 0, max: 4, step: 0.25 }}
					noAudioGain
					slots={{
						largeLayout: {
							beforePlayButton: (
								<Tooltip.Root>
									<Tooltip.Trigger asChild>
										<SeekButton className="vds-button" seconds={-5}>
											<span className="icon-new icon-seek-backward icon-32 vds-icon" />
										</SeekButton>
									</Tooltip.Trigger>
									<Tooltip.Content className="vds-tooltip-content" placement="top">
										Seek -5
									</Tooltip.Content>
								</Tooltip.Root>
							),
							afterPlayButton: (
								<Tooltip.Root>
									<Tooltip.Trigger asChild>
										<SeekButton className="vds-button" seconds={5}>
											<span className="icon-new icon-seek-forward icon-32 vds-icon" />
										</SeekButton>
									</Tooltip.Trigger>
									<Tooltip.Content className="vds-tooltip-content" placement="top">
										Seek +5
									</Tooltip.Content>
								</Tooltip.Root>
							),
							...(clip.url && {
								downloadButton: (
									<Tooltip.Root>
										<Tooltip.Trigger asChild>
											<a
												className="vds-button"
												href={clip.url}
												onTouchEnd={() => window.location.replace(String(clip.url))}
												onClick={() => window.location.replace(String(clip.url))}
											>
												{defaultLayoutIcons.DownloadButton?.Default({})}
											</a>
										</Tooltip.Trigger>
										<Tooltip.Content className="vds-tooltip-content" placement="top">
											Download
										</Tooltip.Content>
									</Tooltip.Root>
								),
							}),
						},
					}}
				/>
			</MediaPlayer>
		</div>
	);
}
